import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Snackbar } from "@mui/material";
import { RTL } from "../cache/CacheRtl";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { useMessageContext } from "../contexts/MessageContext";
import { getErrorMessage } from "../enums/ErrorMessages";
import KpiRow from "../componenets/dashboard/coach/KpiRow";
import TabsRow from "../componenets/dashboard/coach/TabsRow";
import DashboardTable from "../componenets/dashboard/coach/DashboardTable";
import WorkoutsTab, { WorkoutsFilters } from "../componenets/dashboard/coach/WorkoutsTab";
import NutritionTab, { NutritionFilters } from "../componenets/dashboard/coach/NutritionTab";
import StepsTab, { StepsFilters } from "../componenets/dashboard/coach/StepsTab";
import WeightTab, { WeightFilters } from "../componenets/dashboard/coach/WeightTab";
import CheckInsTab, { CheckInsFilters } from "../componenets/dashboard/coach/CheckInsTab";
import CheckInItemDialog from "../componenets/dashboard/coach/CheckInItemDialog";
import ClientsTab from "../componenets/dashboard/coach/ClientsTab";
import ClientsTabFilters from "../componenets/dashboard/coach/ClientsTabFilters";
import { getTrainers } from "../services/TrainerService";
import PushComposer from "../componenets/dashboard/coach/PushComposer";
import WeekPills from "../componenets/dashboard/coach/WeekPills";
import TraineeProfileDialog from "../componenets/dashboard/coach/TraineeProfileDialog";
import ClientActionMenu from "../componenets/client/ClientActionMenu";
import SendPushDialog from "../componenets/dashboard/coach/SendPushDialog";
import { t } from "../componenets/dashboard/coach/coachTokens";
import { getWeeklyWorkoutSummary, getWeeklyNutritionSummary, getWeeklyStepsSummary, getWeeklyWeightSummary, getWeeklyCheckInSummary, getWeeklyClientsSummary } from "../services/CoachDashboardService";
import { getEnhancedStats } from "../services/TraineeService";
import { getCheckInStats } from "../services/CheckInService";

// Page size used by the infinite-scroll fetch. Small batch so each page renders
// fast; once the user scrolls past the last row, the next page is fetched and
// appended automatically by the IntersectionObserver inside DashboardTable.
const DEFAULT_SIZE = 6;

const ComingSoon = ({ tab }) => (
  <Box sx={{ padding: "60px", textAlign: "center", color: t.t3, fontSize: "14px" }}>
    הטאב "{tab}" יבוא בקרוב
  </Box>
);

const CoachDashboard = () => {
  const { isAdmin } = useAuth();
  const { company } = useCompany();
  const { notificationHandler } = useMessageContext();

  const [tab, setTab] = useState("clients");
  const [clientsStateFilter, setClientsStateFilter] = useState("ACTIVE");
  const [clientsTrainerFilter, setClientsTrainerFilter] = useState(0);
  const [trainers, setTrainers] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [workoutFilter, setWorkoutFilter] = useState("all");
  const [nutritionFilter, setNutritionFilter] = useState("all");
  const [stepsFilter, setStepsFilter] = useState("all");
  const [weightFilter, setWeightFilter] = useState("all");
  const [checkInsFilter, setCheckInsFilter] = useState("all");

  // Server-side pagination state
  const [page, setPage] = useState(0);
  // Page size is fixed since the table now uses infinite scroll (the size
  // selector was part of the removed PaginationBar).
  const size = DEFAULT_SIZE;
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);

  // Sort state: serverSort applies to "name". clientSort applies to derived columns.
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("ASC");

  // Time window: 0 = current calendar week, -1 = last week
  const [weekOffset, setWeekOffset] = useState(0);

  const [kpiStats, setKpiStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [refetchTick, setRefetchTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [snack, setSnack] = useState(null);

  // Dialogs
  const [profileTrainee, setProfileTrainee] = useState(null);
  const [actionMenu, setActionMenu] = useState({ anchor: null, trainee: null });
  const [pushTrainee, setPushTrainee] = useState(null);
  const [checkInItem, setCheckInItem] = useState(null); // { checkInId, traineeId, itemType }

  // Debounce search input -> server param
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // KPI stats (total / active this week / inactive / expiring) — fetched per company.
  const [activeEnabledCount, setActiveEnabledCount] = useState(null);
  // "Expiring soon" window — coach picks 7/14/30 days via the KPI badge dropdown.
  const [expiringSoonDays, setExpiringSoonDays] = useState(30);
  // Check-in stats (drives the "צ׳ק-אינס הוגשו השבוע" KPI tile).
  const [checkInStats, setCheckInStats] = useState(null);
  useEffect(() => {
    getEnhancedStats(company?.id ?? null, expiringSoonDays)
      .then((res) => setKpiStats(res.data))
      .catch(() => setKpiStats(null));
  }, [company?.id, expiringSoonDays]);
  useEffect(() => {
    getCheckInStats(company?.id ?? null)
      .then((res) => setCheckInStats(res.data))
      .catch(() => setCheckInStats(null));
  }, [company?.id]);
  useEffect(() => {
    // Cheap second call: ACTIVE filter, size=1 — we only read total.
    getWeeklyClientsSummary({ companyId: company?.id ?? null, page: 0, size: 1, state: "ACTIVE" })
      .then((res) => setActiveEnabledCount(res.data?.total ?? null))
      .catch(() => setActiveEnabledCount(null));
  }, [company?.id]);

  // Load trainer list for admins so the clients tab can filter by trainer.
  useEffect(() => {
    if (!isAdmin()) return;
    getTrainers(company?.id ?? null)
      .then((r) => setTrainers(r.data || []))
      .catch(() => setTrainers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  // Reset page to 0 when search, sort, size, week window, or filters change
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) return;
    setPage(0);
  }, [debouncedSearch, sortKey, sortDir, size, weekOffset, clientsStateFilter, clientsTrainerFilter, workoutFilter, nutritionFilter, stepsFilter, weightFilter, checkInsFilter]);

  useEffect(() => {
    firstLoad.current = false;
    setLoading(true);

    const baseArgs = {
      companyId: company?.id ?? null,
      keyword: debouncedSearch,
      page,
      size,
      sortKey,
      sortDirection: sortDir,
      // Apply the dashboard-wide trainee filters (status + trainer) on every tab.
      state: clientsStateFilter,
      trainerId: clientsTrainerFilter || null,
    };

    let promise;
    if (tab === "clients") {
      promise = getWeeklyClientsSummary({
        ...baseArgs,
        weekOffset,
        closeToEndDays: clientsStateFilter === "CLOSE_TO_END" ? expiringSoonDays : null,
      });
    } else if (tab === "workouts") {
      promise = getWeeklyWorkoutSummary({ ...baseArgs, weekOffset, filter: workoutFilter });
    } else if (tab === "nutrition") {
      promise = getWeeklyNutritionSummary({ ...baseArgs, weekOffset, filter: nutritionFilter });
    } else if (tab === "steps") {
      promise = getWeeklyStepsSummary({ ...baseArgs, weekOffset, filter: stepsFilter });
    } else if (tab === "weight") {
      promise = getWeeklyWeightSummary({ ...baseArgs, weekOffset, filter: weightFilter });
    } else if (tab === "checkins") {
      promise = getWeeklyCheckInSummary({ ...baseArgs, weekOffset, filter: checkInsFilter });
    } else {
      setLoading(false);
      return;
    }

    promise
      .then((res) => {
        const data = res.data || {};
        const incoming = data.content || [];
        // Append when scrolling into the next page; replace on first page so
        // filter/sort/search changes don't dupe the previous result set.
        //
        // The append MUST be idempotent on traineeId. `page` alone is not a safe
        // guard: refetchTick / expiringSoonDays re-run this effect without resetting
        // page to 0 (they're fetch deps but not page-reset deps), and a re-created
        // IntersectionObserver can re-fire on an already-intersecting sentinel. Both
        // re-deliver a page that is already in `rows` — concatenating it blindly is
        // what rendered the same trainee twice on the dashboard.
        setRows((prev) => {
          if (page === 0) return incoming;
          const seen = new Set(prev.map((r) => r.traineeId));
          return [...prev, ...incoming.filter((r) => !seen.has(r.traineeId))];
        });
        setTotal(data.total || 0);
        setHasNext(!!data.hasNext);
      })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)))
      .finally(() => {
        fetchInFlight.current = false;
        setLoading(false);
      });
  }, [tab, company?.id, debouncedSearch, page, size, sortKey, sortDir, weekOffset, clientsStateFilter, clientsTrainerFilter, workoutFilter, nutritionFilter, stepsFilter, weightFilter, checkInsFilter, refetchTick, expiringSoonDays]);

  useEffect(() => {
    setSelected(new Set());
    // Reset the per-tab inline filter chips. Dashboard-wide filters (search,
    // status, trainer, week) intentionally persist across tab switches.
    setWorkoutFilter("all");
    setNutritionFilter("all");
    setStepsFilter("all");
    setWeightFilter("all");
    setCheckInsFilter("all");
    setPage(0);
    setSortKey("name");
    setSortDir("ASC");
  }, [tab]);

  const idToName = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.traineeId, r.traineeName));
    return m;
  }, [rows]);

  // Infinite-scroll trigger: bump the page when the sentinel scrolls into view.
  // The fetch effect appends the next page onto the existing rows.
  // A fetch is in flight. Kept in a ref (not just `loading` state) because the
  // IntersectionObserver callback closes over its creation-time render — a stale
  // `loading === false` there would let the sentinel bump the page against a request
  // that hasn't landed yet, advancing twice for one response.
  const fetchInFlight = useRef(false);
  const onReachEnd = useCallback(() => {
    if (fetchInFlight.current || loading) return;
    if (!hasNext) return;
    fetchInFlight.current = true;
    setPage((p) => p + 1);
  }, [loading, hasNext]);

  const onToggleSelect = (id, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onToggleAll = (visibleRows, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      visibleRows.forEach((r) => {
        if (on) next.add(r.traineeId);
        else next.delete(r.traineeId);
      });
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const onSent = (count, err) => {
    if (err) {
      setSnack({ msg: "שליחה נכשלה", color: t.red });
      return;
    }
    setSnack({ msg: `✅ הפוש נשלח ל-${count} מתאמנים`, color: t.text });
    clearSelection();
  };

  const onSortChange = useCallback((key) => {
    setSortKey((prevKey) => {
      setSortDir((prevDir) => {
        if (prevKey === key) return prevDir === "ASC" ? "DESC" : "ASC";
        return "ASC";
      });
      return key;
    });
  }, []);

  const kpi = useMemo(
    () => ({
      // From getEnhancedStats — accurate across the whole scope, not just the loaded page.
      totalTrainees: kpiStats?.total ?? null,
      activeEnabled: activeEnabledCount,
      activeTrainees: kpiStats?.activeThisWeek ?? null,
      pendingApproval: kpiStats?.withPendingApproval ?? null,
      expiringSoon: kpiStats?.expiringSoon ?? null,
      inactive: kpiStats?.inactive ?? null,
      checkInsSubmittedThisWeek: checkInStats?.submittedThisWeek ?? null,
    }),
    [kpiStats, activeEnabledCount, checkInStats]
  );

  // KPI card click handler — supports (tab, stateFilter) for the Clients tab drill-down.
  const onKpiSelect = useCallback((targetTab, stateFilter) => {
    setTab(targetTab);
    if (targetTab === "clients") {
      setClientsStateFilter(stateFilter);
    }
  }, []);

  // Sorting is fully server-side. Filter chips still apply on the current page.
  // All per-tab filters now apply server-side (see getWeekly*Summary calls in
  // the fetch effect) so pagination + counts stay accurate across pages.
  const visibleRows = rows;

  // Trainee filters (status + trainer) shared across every tab so the filter
  // selection persists when the coach switches tabs.
  const traineeFilters = (
    <ClientsTabFilters
      state={clientsStateFilter}
      onState={setClientsStateFilter}
      trainerId={clientsTrainerFilter}
      onTrainer={setClientsTrainerFilter}
      trainers={trainers}
      showTrainerFilter={isAdmin()}
    />
  );

  return (
    <RTL>
      <Box dir="rtl" sx={{ background: t.bg, minHeight: "100%" }}>
        {/* The full-page Loader is gone; DashboardTable now shows a localized
            overlay on first-page fetches and a small footer spinner on infinite-
            scroll pagination — no full-screen flash between page-0 fetches. */}

        <KpiRow
          stats={kpi}
          onSelect={onKpiSelect}
          expiringSoonDays={expiringSoonDays}
          onExpiringSoonDaysChange={setExpiringSoonDays}
        />

        <TabsRow value={tab} onChange={setTab} />

        {tab === "workouts" && (
          <>
            <DashboardTable
              count={total}
              search={search}
              onSearchChange={setSearch}
              extraFilters={traineeFilters}
              hasMore={hasNext}
              loading={loading && page === 0}
              loadingMore={loading && page > 0}
              onReachEnd={onReachEnd}
              filterChips={
                <>
                  <WorkoutsFilters filter={workoutFilter} onChange={setWorkoutFilter} />
                  <WeekPills value={weekOffset} onChange={setWeekOffset} />
                </>
              }
            >
              <WorkoutsTab
                rows={visibleRows}
                selected={selected}
                onToggleSelect={onToggleSelect}
                onToggleAll={onToggleAll}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onOpenProfile={(r) => setProfileTrainee(r)}
                onOpenActions={(anchor, r) => setActionMenu({ anchor, trainee: r })}
              />
            </DashboardTable>
          </>
        )}

        {tab === "nutrition" && (
          <>
            <DashboardTable
              count={total}
              search={search}
              onSearchChange={setSearch}
              extraFilters={traineeFilters}
              hasMore={hasNext}
              loading={loading && page === 0}
              loadingMore={loading && page > 0}
              onReachEnd={onReachEnd}
              filterChips={
                <>
                  <NutritionFilters filter={nutritionFilter} onChange={setNutritionFilter} />
                  <WeekPills value={weekOffset} onChange={setWeekOffset} />
                </>
              }
            >
              <NutritionTab
                rows={visibleRows}
                selected={selected}
                onToggleSelect={onToggleSelect}
                onToggleAll={onToggleAll}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onOpenProfile={(r) => setProfileTrainee(r)}
                onOpenActions={(anchor, r) => setActionMenu({ anchor, trainee: r })}
              />
            </DashboardTable>
          </>
        )}

        {tab === "steps" && (
          <>
            <DashboardTable
              count={total}
              search={search}
              onSearchChange={setSearch}
              extraFilters={traineeFilters}
              hasMore={hasNext}
              loading={loading && page === 0}
              loadingMore={loading && page > 0}
              onReachEnd={onReachEnd}
              filterChips={
                <>
                  <StepsFilters filter={stepsFilter} onChange={setStepsFilter} />
                  <WeekPills value={weekOffset} onChange={setWeekOffset} />
                </>
              }
            >
              <StepsTab
                rows={visibleRows}
                selected={selected}
                onToggleSelect={onToggleSelect}
                onToggleAll={onToggleAll}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onOpenProfile={(r) => setProfileTrainee(r)}
                onOpenActions={(anchor, r) => setActionMenu({ anchor, trainee: r })}
              />
            </DashboardTable>
          </>
        )}

        {tab === "weight" && (
          <>
            <DashboardTable
              count={total}
              search={search}
              onSearchChange={setSearch}
              extraFilters={traineeFilters}
              hasMore={hasNext}
              loading={loading && page === 0}
              loadingMore={loading && page > 0}
              onReachEnd={onReachEnd}
              filterChips={
                <>
                  <WeightFilters filter={weightFilter} onChange={setWeightFilter} />
                  <WeekPills value={weekOffset} onChange={setWeekOffset} />
                </>
              }
            >
              <WeightTab
                rows={visibleRows}
                selected={selected}
                onToggleSelect={onToggleSelect}
                onToggleAll={onToggleAll}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onOpenProfile={(r) => setProfileTrainee(r)}
                onOpenActions={(anchor, r) => setActionMenu({ anchor, trainee: r })}
              />
            </DashboardTable>
          </>
        )}

        {tab === "checkins" && (
          <>
            <DashboardTable
              count={total}
              search={search}
              onSearchChange={setSearch}
              extraFilters={traineeFilters}
              hasMore={hasNext}
              loading={loading && page === 0}
              loadingMore={loading && page > 0}
              onReachEnd={onReachEnd}
              filterChips={
                <>
                  <CheckInsFilters filter={checkInsFilter} onChange={setCheckInsFilter} />
                  <WeekPills value={weekOffset} onChange={setWeekOffset} />
                </>
              }
            >
              <CheckInsTab
                rows={visibleRows}
                selected={selected}
                onToggleSelect={onToggleSelect}
                onToggleAll={onToggleAll}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onOpenProfile={(r) => setProfileTrainee(r)}
                onOpenActions={(anchor, r) => setActionMenu({ anchor, trainee: r })}
                onOpenItem={(payload) => setCheckInItem(payload)}
              />
            </DashboardTable>
          </>
        )}

        {tab === "clients" && (
          <>
            <DashboardTable
              count={total}
              search={search}
              onSearchChange={setSearch}
              extraFilters={traineeFilters}
              hasMore={hasNext}
              loading={loading && page === 0}
              loadingMore={loading && page > 0}
              onReachEnd={onReachEnd}
              filterChips={
                <WeekPills value={weekOffset} onChange={setWeekOffset} />
              }
            >
              <ClientsTab
                rows={rows}
                selected={selected}
                onToggleSelect={onToggleSelect}
                onToggleAll={onToggleAll}
                sortKey={sortKey}
                sortDir={sortDir}
                onSortChange={onSortChange}
                onOpenProfile={(r) => setProfileTrainee(r)}
                onOpenActions={(anchor, r) => setActionMenu({ anchor, trainee: r })}
                onRowChanged={(patch) => {
                  setRows((prev) => prev.map((r) => r.traineeId === patch.traineeId ? { ...r, ...patch } : r));
                  if ("reviewedThisWeek" in patch) setRefetchTick((n) => n + 1);
                }}
              />
            </DashboardTable>
          </>
        )}

        {tab !== "clients" && tab !== "workouts" && tab !== "nutrition" && tab !== "steps" && tab !== "weight" && tab !== "checkins" && (
          <DashboardTable count={0} search={search} onSearchChange={setSearch}>
            <ComingSoon tab={tab} />
          </DashboardTable>
        )}

        <PushComposer
          selectedIds={selected}
          idToName={idToName}
          onClear={clearSelection}
          onSent={onSent}
        />

        <TraineeProfileDialog
          open={!!profileTrainee}
          onClose={() => setProfileTrainee(null)}
          traineeId={profileTrainee?.traineeId}
          weekOffset={weekOffset}
          onSendPush={(detail) => {
            setPushTrainee(detail);
            setProfileTrainee(null);
          }}
        />

        <ClientActionMenu
          trainee={actionMenu.trainee ? {
            id: actionMenu.trainee.traineeId,
            name: actionMenu.trainee.traineeName,
            enabled: actionMenu.trainee.enabled,
            companyId: company?.id ?? null,
          } : null}
          anchorEl={actionMenu.anchor}
          onClose={() => setActionMenu((prev) => ({ ...prev, anchor: null }))}
          onChanged={() => {
            setActionMenu({ anchor: null, trainee: null });
            setRefetchTick((n) => n + 1);
          }}
        />

        <SendPushDialog
          open={!!pushTrainee}
          onClose={() => setPushTrainee(null)}
          trainee={pushTrainee}
          onSent={(err) => {
            if (err) setSnack({ msg: "שליחה נכשלה", color: t.red });
            else setSnack({ msg: `✅ נשלח ל-${pushTrainee?.traineeName}`, color: t.text });
          }}
        />

        <CheckInItemDialog
          open={!!checkInItem}
          onClose={() => setCheckInItem(null)}
          checkInId={checkInItem?.checkInId}
          traineeId={checkInItem?.traineeId}
          itemType={checkInItem?.itemType}
        />

        <Snackbar
          open={!!snack}
          autoHideDuration={3000}
          onClose={() => setSnack(null)}
          message={snack?.msg}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Box>
    </RTL>
  );
};

export default CoachDashboard;
